import { useState, useEffect } from "react";
import { X, Plus, ArrowLeft } from "lucide-react";
import { getSOSTelefonos, SOSTelefono } from "../../../api/sosService";
import { useAuth } from "../../../context/AuthContext";
import { CrearTelefonoModal } from "./CrearTelefonoModal";
import { EditarTelefonoModal } from "./EditarTelefonoModal";

interface SOSTelefonosModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function SOSTelefonosModal({ isOpen, onClose }: SOSTelefonosModalProps) {
  const { session } = useAuth();
  const [telefonos, setTelefonos] = useState<SOSTelefono[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCrearModalOpen, setIsCrearModalOpen] = useState(false);
  const [telefonoEditando, setTelefonoEditando] = useState<SOSTelefono | null>(null);

  useEffect(() => {
    if (isOpen) {
      fetchTelefonos();
    }
  }, [isOpen]);

  const fetchTelefonos = async () => {
    if (!session?.unidad?.id) return;
    setLoading(true);
    try {
      const data = await getSOSTelefonos(session.unidad.id);
      setTelefonos(data);
    } catch (error) {
      console.error("Error al cargar telefonos:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatedOrModified = () => {
    setIsCrearModalOpen(false);
    setTelefonoEditando(null);
    fetchTelefonos();
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl overflow-hidden flex flex-col max-h-[90vh]">
          {/* Header */}
          <div className="flex justify-between items-center p-4 border-b bg-gray-50">
            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700 transition"
                title="Volver"
              >
                <ArrowLeft size={20} />
              </button>
              <h2 className="text-lg font-semibold text-gray-800">
                Teléfonos de Emergencia
              </h2>
            </div>
            <button
              onClick={() => setIsCrearModalOpen(true)}
              className="flex items-center gap-1 bg-blue-500 text-white px-3 py-1.5 rounded-md text-sm font-medium hover:bg-blue-600 transition shadow-sm"
              title="Añadir Teléfono"
            >
              <Plus size={16} /> Crear
            </button>
          </div>

          {/* Body */}
          <div className="p-4 overflow-y-auto flex-1 bg-gray-50">
            {loading ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              </div>
            ) : telefonos.length === 0 ? (
              <div className="text-center py-12 text-gray-500 bg-white rounded-lg border border-dashed border-gray-300">
                <p>No hay teléfonos configurados.</p>
                <p className="text-sm mt-1">Haga clic en el botón "Crear" para añadir uno nuevo.</p>
              </div>
            ) : (
              <div className="overflow-x-auto bg-white rounded-lg border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Código</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Entidad (Nombre)</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Teléfono</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Prioridad</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Activo</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {telefonos.map((tel) => (
                      <tr 
                        key={tel.ste_codigo} 
                        className="hover:bg-blue-50 cursor-pointer transition"
                        onClick={() => setTelefonoEditando(tel)}
                        title="Click para editar"
                      >
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{tel.ste_codigo}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 flex items-center gap-2">
                          <img src={tel.ste_imagen_nombre} alt="Icono" className="w-5 h-5 object-contain" onError={(e) => (e.currentTarget.style.display = 'none')} />
                          {tel.ste_entidad}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 font-mono">{tel.ste_telefono}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-center text-gray-500">{tel.ste_prioridad}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-center">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            tel.ste_activado === 1 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {tel.ste_activado_nombre}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="text-xs text-gray-500 mt-2 text-center">Haz clic sobre un registro para editarlo.</p>
          </div>
        </div>
      </div>

      <CrearTelefonoModal
        isOpen={isCrearModalOpen}
        onClose={() => setIsCrearModalOpen(false)}
        onCreated={handleCreatedOrModified}
      />
      
      {telefonoEditando && (
        <EditarTelefonoModal
          isOpen={true}
          telefono={telefonoEditando}
          onClose={() => setTelefonoEditando(null)}
          onModified={handleCreatedOrModified}
        />
      )}
    </>
  );
}
